import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import './IDCard.css';
import idCardRegular from '../assets/id-card-regular.png';
import idCardCrew from '../assets/id-card-crew.png';
import pdpBanner from '../assets/id-banners/pdp.svg';
import speakerBanner from '../assets/id-banners/speaker.svg';
import { API_DEFAULTS } from '../constants/api';
import { UI_CONSTANTS, USER_ROLES, ROUTES } from '../constants/app';
import { employeeApi, getErrorMessage } from '../utils/api';
import WebGLBackground from './WebGLBackground';

function IDCard() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const employeeData = location.state?.employeeData;
  const [isAnimated, setIsAnimated] = useState(false);


  // Trigger animation on component mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimated(true);
    }, UI_CONSTANTS.ANIMATION_DELAY);

    return () => clearTimeout(timer);
  }, []);

  const handleBack = () => {
    navigate(ROUTES.HOME);
  };

  // Check if ID card has already been printed by looking at goodies data
  const isIdCardAlreadyPrinted = () => {
    if (!employeeData?.goodies || !Array.isArray(employeeData.goodies)) {
      return false;
    }
    
    // Look for goodie with name "id" and check if received is true
    const idGoodie = employeeData.goodies.find(goodie => {
      return goodie.name === 'id';
    });
    
    // Return true if ID goodie exists and has been received
    return idGoodie && idGoodie.received === true;
  };

  const handlePrint = async () => {
    try {
      // Send print API call
      await employeeApi.print(employeeData.emp_id);
      
      // Trigger browser print dialog
      window.print();
      
      // Navigate to goodies page after print dialog is closed
      // We use a timeout to allow the print dialog to complete
      setTimeout(() => {
        navigate(ROUTES.GOODIES, { state: { employeeData } });
      }, UI_CONSTANTS.PRINT_DIALOG_DELAY);
    } catch (error) {
      console.error('Print API call failed:', error);
      // Still allow printing even if API call fails
      window.print();
      
      setTimeout(() => {
        navigate(ROUTES.GOODIES, { state: { employeeData } });
      }, UI_CONSTANTS.PRINT_DIALOG_DELAY);
    }
  };

  // Determine card type based on crew status
  const cardType = employeeData?.is_crew ? idCardCrew : idCardRegular;
  
  // Create QR code data in the specified format
  const qrCodeData = JSON.stringify({
    name: `${employeeData?.first_name || ''} ${employeeData?.last_name || ''}`.trim(),
    empId: employeeData?.emp_id || '',
    website: employeeData?.eventWebsite || API_DEFAULTS.EVENT_WEBSITE
  });

  // Don't render if no employee data
  if (!employeeData) {
    return (
      <div className="id-card-page">
        <div className="error-message">
          No employee data found. Please go back and try again.
        </div>
        <button className="back-button-floating" onClick={handleBack}>
          ← Back
        </button>
      </div>
    );
  }

  return (
    <div className="id-card-page">
      <div className='print-area'>
        <div className='id-card-print-info'>
          <div className="id-f-name">{employeeData.first_name}</div>
          <div className="id-l-name">{employeeData.last_name}</div>
          <div className="id-dept">{employeeData.department || 'India Team'}</div>
          <div className="qr-code-container">
            <QRCodeCanvas 
              value={qrCodeData}
              size={UI_CONSTANTS.QR_CODE_SIZE_PRINT}
              level={UI_CONSTANTS.QR_CODE_LEVEL}
              includeMargin={true}
            />
          </div>
        </div>
      </div>
      <div className="id-card-main-content non-print-area">
        <WebGLBackground 
          height="100vh" 
          width="100%" 
          left="0" 
          opacity={0.8}
          zIndex={-1}
        />
        <div className="welcome-section">
          <h1 className="welcome-title">Hello {employeeData.first_name || ''}</h1>
          <p className="welcome-message">
            Your ID Card is ready.<br />
            Grab them now!
          </p>
          <div>
            <button className={`print-now-button ${isAnimated ? 'fade-in' : ''}`} onClick={handlePrint}>
            {isIdCardAlreadyPrinted() ? 'Reprint ID Card' : 'Print ID Card'}
          </button>
          </div>
          
        </div>

        <div className="id-card-preview-section">
          <div className={`id-card-display ${isAnimated ? 'slide-down' : ''}`}>
            <img 
              src={cardType} 
              alt="ID Card"
              className="id-card-image"
            />

            <div className='id-card-print-info'>
              <div className="id-f-name">{employeeData.first_name}</div>
              <div className="id-l-name">{employeeData.last_name}</div>
              <div className="id-dept">{employeeData.department}</div>
              <div className="qr-code-container">
                <QRCodeCanvas 
                  value={qrCodeData}
                  size={UI_CONSTANTS.QR_CODE_SIZE}
                  level={UI_CONSTANTS.QR_CODE_LEVEL}
                  includeMargin={true}
                />
              </div>
            </div>

            <div className={`id-banners speaker-${employeeData?.is_speaker} pdp-${employeeData?.is_pdp}`}>
              {employeeData?.is_speaker && <img src={speakerBanner} alt="Speaker Banner" className="id-banner" />}
              {employeeData?.is_pdp && <img src={pdpBanner} alt="PDP Banner" className="id-banner" />}
            </div>
          </div>
        </div>

         <button className="back-button-floating" onClick={handleBack}>
          ← Back
        </button>
      </div>

     
    </div>
  );
}

export default IDCard;
